import React, { useState, useEffect, useContext } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { X } from 'lucide-react';
import Select from 'react-select';
import { CampaignProps } from './types/propTypes';
import { useHttp } from 'src/hooks';
import { AuthContext } from 'src/contexts';

const validationSchema = Yup.object().shape({
  targetLocations: Yup.array()
    .min(1, 'Please add at least 1 location')
    .max(5, 'You can add a maximum of 5 locations')
    .required('Location is required'),
  targetCountries: Yup.array()
    .min(1, 'Please add at least 1 country')
    .max(5, 'You can add a maximum of 5 countries')
    .required('Country is required'),
  keywords: Yup.array()
    .min(5, 'Please add at least 5 keywords')
    .required('Keywords are required'),
});

const TargetAudienceForm: React.FC<CampaignProps> = ({
  setCampaignData,
  campaignData,
  setIsStepValid,
}) => {
  const { sendRequest } = useHttp();
  const { accessToken } = useContext(AuthContext);
  const [keywords, setKeywords] = useState<string[]>(campaignData?.keywords || []);
  const [locations, setLocations] = useState<string[]>(campaignData?.targetLocations || []);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(
    campaignData?.targetCountries || [],
  );
  const [keywordInput, setKeywordInput] = useState('');
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLocations = async () => {
      if (!accessToken) return;

      setLoading(true);

      sendRequest(
        {
          url: `facebook/geo-locations?search=${encodeURIComponent(search)}`,
          accessToken,
        },
        data => {
          if (data?.data) {
            const formattedLocations = data.data.map((item: any) => ({
              value: item.key,
              label: item.name,
              country: item.name,
            }));
            setLocationOptions(formattedLocations);
          }
          setLoading(false);
        },
        () => setLoading(false),
      );
    };

    fetchLocations();
  }, [search]);

  return (
    <div className="my-8 p-6 border border-[#E5E7E7] rounded-2xl">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-[28px] text-center font-semibold font-inter text-black mb-10">
          Tell us about your target audience
        </h2>
        <Formik
          initialValues={{
            targetLocations: campaignData?.targetLocations || [],
            targetCountries: campaignData?.targetCountries || [],
            keywords: campaignData?.keywords || [],
          }}
          validationSchema={validationSchema}
          onSubmit={values => {
            console.log('Form submitted with values:', values);
          }}
        >
          {({ setFieldValue, values, isValid, dirty }) => {
            // Moved handleKeywordSubmit inside Formik render function
            const handleKeywordSubmit = () => {
              if (keywordInput.trim()) {
                // Split by comma or newline and trim each keyword
                const newKeywordsArray = keywordInput
                  .split(/[\n,]+/)
                  .map(keyword => keyword.trim())
                  .filter(keyword => keyword.length > 0);
                
                // Remove duplicates from the new input (case-insensitive)
                const uniqueNewKeywords = newKeywordsArray.filter((keyword, index, self) => 
                  index === self.findIndex(k => k.toLowerCase() === keyword.toLowerCase())
                );
                
                // Filter out keywords that already exist in the current list
                const nonDuplicateKeywords = uniqueNewKeywords.filter(
                  keyword => !keywords.some(k => k.toLowerCase() === keyword.toLowerCase())
                );

                if (nonDuplicateKeywords.length > 0) {
                  const newKeywords = [...keywords, ...nonDuplicateKeywords];
                  setKeywords(newKeywords);
                  setFieldValue('keywords', newKeywords);
                  setKeywordInput('');
                } else if (uniqueNewKeywords.length > 0) {
                  // If all keywords are duplicates, just clear the input
                  setKeywordInput('');
                }
              }
            };

            useEffect(() => {
              setCampaignData(prev => ({
                ...prev,
                ...values,
              }));

              // Check if we have at least 5 keywords AND at least 1 country
              const hasEnoughKeywords = values.keywords.length >= 5;
              const hasCountry = values.targetCountries.length >= 1;
              const isStepValid = hasEnoughKeywords && hasCountry;

              setIsStepValid(isStepValid);
            }, [values, setIsStepValid, setCampaignData]);

            return (
              <Form className="space-y-6">
                {/* Keywords Input - Changed to Textarea */}
                <div>
                  <label className="block text-[#4A4D4F] text-base font-normal font-inter mb-2">
                    Enter at least 5 keywords that refer to your target audience
                    <span className="text-sm text-gray-500 ml-2">
                      (Separate with commas or new lines)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {keywords.map((keyword, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-gray-200 text-gray-800 px-3 py-1 rounded-lg"
                      >
                        {keyword}
                        <button
                          type="button"
                          className="ml-2 text-gray-600 hover:text-red-500"
                          onClick={() => {
                            const newKeywords = keywords.filter((_, i) => i !== index);
                            setKeywords(newKeywords);
                            setFieldValue('keywords', newKeywords);
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <textarea
                    placeholder="Enter keywords separated by commas or new lines"
                    className="w-full px-4 py-3 border rounded-lg placeholder-gray-600 text-base font-inter font-normal outline-none resize-none"
                    rows={4}
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        e.preventDefault();
                        handleKeywordSubmit();
                      }
                    }}
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      onClick={handleKeywordSubmit}
                    >
                      Add Keywords
                    </button>
                  </div>
                  {/* <div className="text-sm text-gray-500 mt-1">
                    {keywords.length}/5+ keywords added
                  </div> */}
                  <ErrorMessage
                    name="keywords"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Searchable Locations Dropdown */}
                <div>
                  <label className="block text-[#4A4D4F] text-base font-normal font-inter mb-2">
                    What Location(s) would you like to target?
                  </label>
                  <Select
                    options={locationOptions}
                    isMulti
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder={loading ? 'Loading...' : 'Search locations...'}
                    value={locations.map((loc, index) => ({
                      value: loc,
                      label: selectedCountries[index] || loc,
                    }))}
                    onChange={selected => {
                      const selectedValues = selected.map(item => item.value);
                      const selectedCountryNames = selected.map(item => item.label);

                      if (selectedValues.length <= 5) {
                        setLocations(selectedValues);
                        setSelectedCountries(selectedCountryNames);
                        setFieldValue('targetLocations', selectedValues);
                        setFieldValue('targetCountries', selectedCountryNames);
                      }
                    }}
                    onInputChange={value => setSearch(value)}
                    isLoading={loading}
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {locations.map((location, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-gray-200 text-gray-800 px-3 py-1 rounded-lg"
                      >
                        {selectedCountries[index] || location}
                        <button
                          type="button"
                          className="ml-2 text-gray-600 hover:text-red-500"
                          onClick={() => {
                            const newLocations = locations.filter((_, i) => i !== index);
                            const newCountries = selectedCountries.filter((_, i) => i !== index);
                            setLocations(newLocations);
                            setSelectedCountries(newCountries);
                            setFieldValue('targetLocations', newLocations);
                            setFieldValue('targetCountries', newCountries);
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* <div className="text-sm text-gray-500 mt-1">
                    {selectedCountries.length}/5 countries selected
                  </div> */}
                  <ErrorMessage
                    name="targetLocations"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                  <ErrorMessage
                    name="targetCountries"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Validation Status */}
                {/* <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">Validation Status:</div>
                  <div className={`text-sm ${keywords.length >= 5 ? 'text-green-600' : 'text-red-600'}`}>
                    ✓ {keywords.length >= 5 ? 'At least 5 keywords added' : 'Need at least 5 keywords'}
                  </div>
                  <div className={`text-sm ${selectedCountries.length >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                    ✓ {selectedCountries.length >= 1 ? 'At least 1 country selected' : 'Need at least 1 country'}
                  </div>
                </div> */}
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default TargetAudienceForm;